import React from 'react'
import {
  Input,
  InputLabel as Label,
  FormError,
  TextArea,
  Checkbox,
  Button,
} from '../../../Common'
import { useForm, Controller } from 'react-hook-form'
import { ContactsPageProps, FormGDPR } from '../../contactsPage.props'
import {
  useContactForm,
  useLocalLabels,
  useProfile,
  useConfigState,
  useUIFunctions,
} from '@sentecacommerce-theme/lib'
import styled from 'styled-components'

import { useToasts } from 'react-toast-notifications'
import { GDPRModalProps } from '../../../Common/Modals/GDPRModal'

export const ContactForm = ({
  linkLabel,
  label,
  modalContent,
}: ContactFormProps) => {
  const { data } = useProfile()
  const { addToast } = useToasts()
  const { enqueueView } = useUIFunctions()
  const config = useConfigState()
  const { labels } = useLocalLabels<ContactsPageProps>()

  const onSuccess = (formaData: any) => {
    if (formaData?.statusCode > 299 || formaData?.statusCode < 200) {
      onError()
      return
    }

    addToast(labels?.successMessage, {
      appearance: 'success',
      autoDismiss: true,
    })
  }

  const onError = () => {
    addToast(labels?.errorMessage, {
      appearance: 'error',
      autoDismiss: true,
    })
  }

  const [sendEmail, { isLoading }] = useContactForm({
    onError,
    onSuccess,
    endpoint: 'email/contact',
    integrationDomain: config.integration,
  })

  const getDefaultValues = (): FormData => {
    if (!data) {
      return {
        email: '',
        about: '',
        phone: '',
        names: '',
        query: '',
        gdprConsent: false,
      }
    }

    return {
      email: data.email || '',
      about: '',
      phone: data.phoneNumber || '',
      names: data.firstName ? `${data.firstName} ${data.lastName || ''}` : '',
      query: '',
      gdprConsent: false,
    }
  }

  const { control, errors, handleSubmit } = useForm({
    defaultValues: getDefaultValues(),
  })

  const onSubmit = (formData: FormData) => {
    sendEmail({
      subject: formData.about,
      email: formData.email,
      phone: formData.phone,
      name: formData.names,
      message: `
      <p>
        От: ${formData.names}
      </p>
      <p>
        ${labels?.emailInputLabel}: ${formData.email}
      </p>
       <p>
        ${labels?.phoneInputLabel}: ${formData.phone}
      </p>
      <p>
        ${labels?.questionInputLabel}: ${formData.about}
      </p>
      <p>
        ${formData.query}
      </p>
      `.trim(),
    })
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup half>
        <Label htmlFor="names">
          {labels?.nameInputLabel} *
          <Controller
            control={control}
            name="names"
            rules={{
              required: labels?.fieldIsRequired,
            }}
            as={<Input id="contact-form-names" />}
          />
          <FormError errors={errors} fieldName="names" />
        </Label>
      </FormGroup>
      <FormGroup half>
        <Label htmlFor="email">
          {labels?.emailInputLabel} *
          <Controller
            control={control}
            name="email"
            rules={{
              required: labels?.fieldIsRequired,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: labels?.invalidEmail,
              },
            }}
            as={<Input id="contact-form-email" />}
          />
          <FormError errors={errors} fieldName="email" />
        </Label>
      </FormGroup>
      <FormGroup>
        <Label htmlFor="phone">
          {labels?.phoneInputLabel} *
          <Controller
            control={control}
            name="phone"
            rules={{
              required: labels?.fieldIsRequired,
            }}
            as={<Input id="contact-form-phone" />}
          />
          <FormError errors={errors} fieldName="phone" />
        </Label>
      </FormGroup>
      <FormGroup>
        <Label htmlFor="about">
          {labels?.aboutInputLabel} *
          <Controller
            control={control}
            name="about"
            rules={{
              required: labels?.fieldIsRequired,
            }}
            as={<Input id="contact-form-about" />}
          />
          <FormError errors={errors} fieldName="about" />
        </Label>
      </FormGroup>
      <FormGroup>
        <Label htmlFor="query">
          {labels?.questionInputLabel} *
          <Controller
            control={control}
            name="query"
            rules={{
              required: labels?.fieldIsRequired,
            }}
            as={<TextArea id="contact-form-query" />}
          />
          <FormError errors={errors} fieldName="query" />
        </Label>
      </FormGroup>
      <FormGroup>
        <Label htmlFor="">
          <GDPRContainer>
            <Controller
              control={control}
              name="gdprConsent"
              rules={{
                required: labels?.fieldIsRequired,
              }}
              render={({ value, onChange }) => (
                <Checkbox
                  onChange={() => onChange(!value)}
                  value={value}
                  isChecked={value}
                  small
                  align="flex-start"
                  label=""
                  name="contact-form-gdprConsent"
                />
              )}
            />
            <GDPRLabel>
              {label}
              <span
                onClick={() =>
                  enqueueView<GDPRModalProps>(config.views.gdprModal, {
                    html: modalContent,
                  })
                }
              >
                {linkLabel}
              </span>
            </GDPRLabel>
          </GDPRContainer>
          <FormError errors={errors} fieldName="gdprConsent" />
        </Label>
      </FormGroup>
      <FormGroup>
        <ButtonContainer>
          <Button
            disabled={isLoading}
            type="submit"
            variant={isLoading ? 'disabled' : 'green'}
          >
            {isLoading ? '...' : labels?.sendLabel}
          </Button>
        </ButtonContainer>
      </FormGroup>
    </Form>
  )
}

type ContactFormProps = FormGDPR & {}

type FormData = {
  email: string
  about: string
  phone: string
  names: string
  query: string
  gdprConsent: boolean
}

const Form = styled.form.attrs({
  className: 'row',
})``

const FormGroup = styled.div.attrs(({ half }: any) => ({
  className: half ? 'col-6' : 'col-12',
}))<{ half?: boolean }>``

const GDPRLabel = styled.p`
  max-width: calc(100% - 2.8rem);
  margin-top: -0.3rem;
  & span {
    color: ${({ theme }) => theme.palette.silverTree};
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
    &:after,
    &:before {
      content: ')';
      color: ${({ theme }) => theme.palette.greyTitle};
    }

    &:before {
      content: ' (';
      color: ${({ theme }) => theme.palette.greyTitle};
    }
  }
`

const ButtonContainer = styled.div.attrs({
  className: 'mt-4',
})`
  display: flex;
  align-items: center;
  justify-content: center;
`

const GDPRContainer = styled.div`
  display: flex;
`
